import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { emailPattern } from "@/Utility/Constants";
import { Loader2 } from "lucide-react";

export function LoginForm({
  onSubmit,
  onGoogleLogin,
  isSubmitLoading = false,
  isGoogleLoading = false,
  initialEmail = "",
  initialPassword = "",
  ...props
}) {
  const isFormDisabled = isSubmitLoading || isGoogleLoading;
  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    const email = form.email?.value?.trim();
    const password = form.password?.value;

    if (!email) {
      form.email?.focus();
      return;
    }
    if (!emailPattern.test(email)) {
      form.email?.setCustomValidity("Please enter a valid email");
      form.email?.reportValidity();
      return;
    }
    form.email?.setCustomValidity("");
    if (!password) {
      form.password?.focus();
      return;
    }

    onSubmit?.({ email, password });
  };

  return (
    <div className="space-y-4">
      <form
        className="flex flex-col"
        onSubmit={handleSubmit}
        name="login"
        {...props}
      >
        <FieldGroup className={"gap-4"}>
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Login to your account</h1>
          </div>
          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="john@example.com"
              defaultValue={initialEmail}
              required
              autoComplete="email"
              disabled={isFormDisabled}
            />
          </Field>
          <Field>
            <div className="flex items-center">
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <Link
                to="/reset-password"
                className="ml-auto text-sm underline-offset-4 hover:underline text-primary"
              >
                Forgot your password?
              </Link>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              required
              autoComplete="current-password"
              defaultValue={initialPassword}
              disabled={isFormDisabled}
            />
          </Field>
          <Field>
            <Button type="submit" className="w-full" disabled={isFormDisabled}>
              {isSubmitLoading ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Login"
              )}
            </Button>
          </Field>
        </FieldGroup>
      </form>
      <div className="space-y-2">
        <Button 
          variant="outline"
          type="button"
          className="w-full"
          onClick={onGoogleLogin}
        >
          {isGoogleLoading ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <GoogleIcon className="mr-2 size-4" />
          )}
          Sign in with Google
        </Button>
        <div className="text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link
            to="/signup"
            className="underline underline-offset-4 text-primary"
          >
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon({ className, ...props }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className={cn("size-4", className)}
      {...props}
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}
